import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  Plus,
  Trash2,
  ExternalLink,
  ShieldCheck,
  Video,
  CreditCard,
  Settings,
} from "lucide-react";
import { t } from "@/i18n";

const PLAN_COPY = {
  monthly: "Accès complet, renouvelé chaque mois.",
  annual: "Le meilleur rapport qualité-prix.",
  lifetime: "Un seul paiement, accès permanent.",
};

function fmtDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("fr-FR");
}

export default function Billing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [claims, setClaims] = useState([]);
  const [urls, setUrls] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [salesConsent, setSalesConsent] = useState(false);

  // --- account state -------------------------------------------------
  const isTrial = user?.account_type === "trial";
  const isExpired = user?.account_type === "expired";
  const isSubscribed = !!user && !isTrial && !isExpired; // has an active paid plan
  const trialDaysLeft = user?.access_days_remaining ?? 0;

  const load = useCallback(async () => {
    try {
      const plans = await api.get("/billing/plans");
      setCatalog(plans.data);
    } catch {
      setCatalog({ plans: [], provider_connected: false });
    }
    if (user) {
      try {
        const submitted = await api.get("/billing/creator-claims");
        setClaims(Array.isArray(submitted.data) ? submitted.data : []);
      } catch {
        setClaims([]);
      }
    }
  }, [user]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const checkout = async (plan) => {
    if (!user) {
      nav("/auth", { state: { from: "/billing" } });
      return;
    }
    if (!salesConsent) {
      toast.error("Accepte les CGV avant de poursuivre.");
      return;
    }
    try {
      await api.post("/billing/checkout", { plan });
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Le paiement n’est pas encore disponible.",
      );
    }
  };

  // Opens the Stripe billing portal so the user can manage/cancel/change plan.
  // Expects a backend endpoint POST /billing/portal -> { url }
  const openPortal = async () => {
    if (!user) {
      nav("/auth", { state: { from: "/billing" } });
      return;
    }
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal");
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Le portail de facturation n’est pas encore disponible.");
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Impossible d’ouvrir le portail de facturation.",
      );
    } finally {
      setPortalLoading(false);
    }
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!user) {
      nav("/auth", { state: { from: "/billing" } });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/billing/creator-claims", {
        email: user.email,
        video_urls: urls.filter(Boolean),
      });
      setUrls([""]);
      await load();
      toast.success("Demande envoyée. Elle sera vérifiée par l’équipe.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible d’envoyer la demande.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const addUrl = () => setUrls((items) => [...items, ""]);
  const updateUrl = (index, value) =>
    setUrls((items) => items.map((item, i) => (i === index ? value : item)));
  const removeUrl = (index) =>
    setUrls((items) =>
      items.length === 1 ? [""] : items.filter((_, i) => i !== index),
    );

  const plans = catalog?.plans?.length
    ? catalog.plans
    : [
        {
          id: "monthly",
          name: "Mensuel",
          price: 7,
          currency: "EUR",
          period: "month",
        },
        {
          id: "annual",
          name: "Annuel",
          price: 70,
          currency: "EUR",
          period: "year",
        },
        {
          id: "lifetime",
          name: "À vie",
          price: 100,
          currency: "EUR",
          period: "once",
        },
      ];

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <section className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary mb-3">
          <ShieldCheck size={14} /> {t("billing.7_jours_d_essai_gratuit_sans")}
        </div>
        <h1 className="font-head text-4xl font-extrabold">
          {t("billing.choisis_ton_acces_harf")}
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
          {t("billing.cree_ton_compte_pour_essayer_gratuitement")}
        </p>
      </section>

      {/* Discrete trial banner with a direct action, shown only during trial */}
      {isTrial && (
        <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-primary">
            {t("billing.periode_d_essai_il_vous_reste")} {trialDaysLeft} jour
            {trialDaysLeft > 1 ? "s" : ""} {t("billing.fin_le")}{" "}
            {fmtDate(user.access_expires_at)})
          </div>
          <button
            onClick={() =>
              document
                .getElementById("plans-grid")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {t("billing.payer_maintenant")}
          </button>
        </section>
      )}

      {isSubscribed ? (
        /* Already subscribed: hide the pricing grid entirely, show a simple manage card */
        <section className="rounded-3xl border bg-card p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-muted-foreground">
              {t("billing.votre_abonnement")}
            </div>
            <div className="font-head text-2xl font-bold">
              {t("admin.plan")}{" "}
              {user.plan
                ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1)
                : ""}{" "}
              {t("billing.actif")}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {user.plan === "lifetime"
                ? t("billing.acces_a_vie")
                : `Valide jusqu’au ${fmtDate(user.access_expires_at)}`}
            </div>
          </div>
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="rounded-xl bg-primary px-5 py-3 font-semibold text-primary-foreground hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
          >
            <Settings size={16} />{" "}
            {portalLoading ? t("billing.ouverture") : t("billing.gerer_mon_abonnement")}
          </button>
        </section>
      ) : (
        <>
          <section className="rounded-2xl border bg-card p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm text-muted-foreground">{t("billing.statut_actuel")}</div>
              <div className="font-head text-xl font-bold">
                {!user
                  ? t("billing.pas_encore_inscrit")
                  : isTrial
                    ? "Essai gratuit"
                    : isExpired
                      ? "Essai terminé"
                      : "Accès actif"}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {!user
                ? t("billing.inscris_toi_pour_commencer_ton_essai")
                : isTrial
                  ? `${trialDaysLeft} jour(s) restant(s) · fin le ${fmtDate(user.access_expires_at)}`
                  : isExpired
                    ? "Les fonctions premium sont verrouillées."
                    : `Accès jusqu’au ${user?.plan === "lifetime" ? "toujours" : fmtDate(user?.access_expires_at)}`}
            </div>
          </section>

          <section id="plans-grid" className="grid md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-3xl border bg-card p-6 ${
                  plan.id === "annual"
                    ? "border-primary ring-2 ring-primary/20"
                    : ""
                }`}
              >
                {plan.id === "annual" && (
                  <div className="text-xs font-bold text-primary mb-2">
                    {t("billing.le_plus_populaire")}
                  </div>
                )}
                <h2 className="font-head text-2xl font-bold">{plan.name}</h2>
                <div className="mt-3 mb-1">
                  <span className="font-head text-4xl font-extrabold">
                    {plan.price}€
                  </span>
                  {plan.period !== "once" && (
                    <span className="text-sm text-muted-foreground">
                      /{plan.period === "month" ? "mois" : "an"}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground min-h-10">
                  {PLAN_COPY[plan.id]}
                </p>
                <ul className="my-5 space-y-2 text-sm">
                  {[
                    "Toutes les leçons et exercices",
                    "Île et progression complète",
                    "Révisions espacées",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <Check size={15} className="text-emerald-600" />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => checkout(plan.id)}
                  className="w-full rounded-xl bg-primary px-4 py-3 font-semibold text-primary-foreground hover:opacity-90 flex items-center justify-center gap-2"
                >
                  <CreditCard size={16} />{" "}
                  {user
                    ? t("billing.choisir_cette_formule")
                    : t("billing.essayer_gratuitement_pendant_une_semaine")}
                </button>
                {!catalog?.provider_connected && (
                  <p className="text-[11px] text-muted-foreground mt-2 text-center">
                    {t("billing.paiement_a_connecter_par_l_administrateur")}
                  </p>
                )}
              </div>
            ))}
          </section>

          <label className="mx-auto flex max-w-2xl items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={salesConsent}
              onChange={(e) => setSalesConsent(e.target.checked)}
              className="mt-0.5 accent-primary"
            />
            <span>
              {t("billing.j_ai_lu_et_j_accepte")}{" "}
              <a href="/legal/cgv" className="text-primary underline">
                {t("billing.cgv")}
              </a>{" "}
              {t("cookieconsent.et_la")}{" "}
              <a href="/legal/retractation" className="text-primary underline">
                {t("billing.politique_de_retractation")}
              </a>
              . Le paiement n’est pas encore activé sur Harf.
            </span>
          </label>
        </>
      )}

      <section className="rounded-3xl border bg-card p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="rounded-2xl bg-emerald-500/10 p-3 text-emerald-600">
            <Video size={22} />
          </div>
          <div>
            <h2 className="font-head text-2xl font-bold">
              {t("billing.gagner_du_temps_premium_gratuitement_avec")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("billing.publie_une_ou_plusieurs_videos_puis")}
            </p>
          </div>
        </div>
        <div className="rounded-2xl bg-secondary/70 p-4 mb-5 text-sm">
          <div className="font-semibold mb-2">{t("billing.ce_que_represente_la_duree")}</div>
          <p className="text-muted-foreground">
            {t("billing.par_exemple_10_000_vues_verifiees")}{" "}
            <strong className="text-foreground">
              {t("billing.1_mois_d_acces_premium_offert")}
            </strong>
            . À 1 000 000 de vues cumulées, tu obtiens un accès permanent.
          </p>
        </div>
        <div className="grid sm:grid-cols-4 gap-2 mb-6 text-center text-xs">
          {[
            ["10 000", "1 mois offert"],
            ["100 000", "6 mois offerts"],
            ["500 000", "1 an offert"],
            ["1 000 000", "Accès à vie"],
          ].map(([views, reward]) => (
            <div key={views} className="rounded-xl bg-secondary p-3">
              <div className="font-bold">{views} {t("billing.vues_cumulees")}</div>
              <div className="text-muted-foreground mt-1">{reward}</div>
            </div>
          ))}
        </div>
        {!user ? (
          <div className="rounded-xl border border-dashed p-4 text-sm">
            <p className="font-semibold">
              {t("billing.tu_veux_demander_cet_acces_gratuit")}
            </p>
            <p className="text-muted-foreground mt-1">
              {t("billing.cree_d_abord_ton_compte_gratuit")}
            </p>
            <button
              type="button"
              onClick={() => nav("/auth", { state: { from: "/billing" } })}
              className="mt-3 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {t("billing.creer_mon_compte_gratuit")}
            </button>
          </div>
        ) : (
          <form onSubmit={submitClaim} className="space-y-3">
            <div className="text-sm font-semibold">
              {t("billing.email_associe")}{" "}
              <span className="font-normal text-muted-foreground">
                {user?.email}
              </span>
            </div>
            {urls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <input
                  required
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl(index, e.target.value)}
                  placeholder="https://youtube.com/… ou lien TikTok/Instagram"
                  className="flex-1 rounded-xl border bg-background px-4 py-2.5 outline-none focus:ring-2 ring-primary"
                />
                <button
                  type="button"
                  onClick={() => removeUrl(index)}
                  className="rounded-xl border px-3 text-destructive hover:bg-destructive/10"
                  aria-label={t("billing.supprimer_ce_lien")}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={addUrl}
                className="rounded-xl border px-3 py-2 text-sm font-medium flex items-center gap-1 hover:bg-secondary"
              >
                <Plus size={15} /> {t("billing.ajouter_une_video")}
              </button>
              <button
                disabled={submitting}
                type="submit"
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                <ExternalLink size={15} /> {t("billing.notifier_mes_statistiques")}
              </button>
            </div>
          </form>
        )}
        {claims.length > 0 && (
          <div className="mt-6 border-t pt-4 space-y-2">
            <h3 className="font-semibold">{t("billing.mes_demandes")}</h3>
            {claims.map((claim) => (
              <div
                key={claim.id}
                className="rounded-xl bg-secondary/60 p-3 text-sm flex flex-wrap justify-between gap-2"
              >
                <span>
                  {claim.video_urls?.length ?? 0} {t("billing.video_s")}{" "}
                  {(claim.verified_views ?? 0).toLocaleString("fr-FR")} {t("billing.vues_verifiees")}
                </span>
                <span
                  className={
                    claim.status === "approved"
                      ? "text-emerald-600 font-semibold"
                      : claim.status === "rejected"
                        ? "text-destructive"
                        : "text-muted-foreground"
                  }
                >
                  {claim.status === "approved"
                    ? `Accès accordé (${claim.reward_plan})`
                    : claim.status === "rejected"
                      ? "Refusée"
                      : "En attente de vérification"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
